import React, { useState, useEffect, useCallback } from "react";
import { Button, Dropdown, Menu, Modal, Form, Input, Select, Divider, message, Empty, DatePicker, InputNumber, Tabs, Space, Tooltip, Alert, Tag } from "antd";
import { DownOutlined, TeamOutlined, FileTextOutlined, ThunderboltOutlined, CoffeeOutlined, FileProtectOutlined, CheckOutlined, EditOutlined, CloseCircleOutlined, CarOutlined, HomeOutlined, MailOutlined } from "@ant-design/icons";
import { TRANSACTION_TYPES, TRANSACTION_TYPE_LABELS, TRANSACTION_STATUS, TRANSACTION_STATUS_LABELS, TRANSACTION_STATUS_COLORS, DEFAULT_TRANSACTION_DETAILS, TRANSACTION_TYPE_SPECIFIC_STATUS, TRANSACTION_CHANGE_TYPES, TRANSACTION_MIGRATION_STATUS } from "../constants";
import { flightOperationTransactionService } from "../../../../api/enhancedApi";
import { emailTemplateApi } from "../../../../api/emailTemplateApi";
import { sendUniversalEmail } from "../../../../api/flightOperationEmailApi";
import TransactionStatusTag from "./TransactionStatusTag";
import PermissionGuard from "../../../../components/auth/PermissionGuard";
import GeneralEmailEditor from "../../../../components/EmailEditor/GeneralEmailEditor";
import UnifiedTransactionEmailDrawer from "./UnifiedTransactionEmailDrawer";

const { TextArea } = Input;
const { TabPane } = Tabs;

/**
 * 批量事务操作组件
 * 用于对多个选中的航班执行批量事务操作
 *
 * @param {Object} props
 * @param {Array} props.selectedFlightIds - 选中的航班ID列表
 * @param {Function} props.onSuccess - 成功处理后的回调函数，用于刷新和高亮航班
 * @param {Array} props.allFlightsData - 包含所有航班详细信息的数组
 * @param {Function} props.onFlightRemove - 从选中列表中移除航班的回调函数
 * @param {Function} props.onOpenBatchTransactionDrawer - V4版本批量事务处理回调函数
 */
const BatchTransactionActions = ({ selectedFlightIds = [], onSuccess, allFlightsData = [], onFlightRemove, onOpenBatchTransactionDrawer }) => {
    const [confirmModalVisible, setConfirmModalVisible] = useState(false);
    const [currentAction, setCurrentAction] = useState({});
    const [form] = Form.useForm();
    const [activeTab, setActiveTab] = useState("details");
    const [isSubmitting, setIsSubmitting] = useState(false);

    // 记录用户真正修改过的字段（包含值）
    const [modifiedFields, setModifiedFields] = useState({});

    // 邮件相关状态
    const [isEmailEditorVisible, setIsEmailEditorVisible] = useState(false);
    const [emailTemplate, setEmailTemplate] = useState(null);
    const [isEmailLoading, setIsEmailLoading] = useState(false);
    const [isSending, setIsSending] = useState(false); // 邮件发送状态
    const [sendingProgress, setSendingProgress] = useState(null); // 发送进度信息

    // 统一事务邮件抽屉状态
    const [isTransactionEmailDrawerVisible, setIsTransactionEmailDrawerVisible] = useState(false);

    // 迁移处理状态
    const [isMigrationProcessing, setIsMigrationProcessing] = useState(false);

    // 监控表单状态
    const departureLandingStatus = Form.useWatch("departureLandingStatus", form);
    const arrivalLandingStatus = Form.useWatch("arrivalLandingStatus", form);
    const overFlightStatus = Form.useWatch("overFlightStatus", form);

    // 存储 tab 状态标签显示
    const [tabStatus, setTabStatus] = useState({
        departureLanding: null, // 初始化为 null
        arrivalLanding: null, // 初始化为 null
        overflight: null, // 初始化为 null
    });

    // 监听状态变化更新 tab 显示 - 确保仅在有值时更新
    useEffect(() => {
        if (departureLandingStatus !== undefined && departureLandingStatus !== null) {
            // 检查是否有实际值
            setTabStatus((prev) => ({ ...prev, departureLanding: departureLandingStatus }));
        }
    }, [departureLandingStatus]);

    useEffect(() => {
        if (arrivalLandingStatus !== undefined && arrivalLandingStatus !== null) {
            // 检查是否有实际值
            setTabStatus((prev) => ({ ...prev, arrivalLanding: arrivalLandingStatus }));
        }
    }, [arrivalLandingStatus]);

    useEffect(() => {
        if (overFlightStatus !== undefined && overFlightStatus !== null) {
            // 检查是否有实际值
            setTabStatus((prev) => ({ ...prev, overflight: overFlightStatus }));
        }
    }, [overFlightStatus]);

    // 检查是否有选中的航班
    const hasSelectedFlights = selectedFlightIds.length > 0;

    // 处理选择事务类型
    const handleSelectTransaction = (transactionType) => {
        setCurrentAction({ transactionType });
        setConfirmModalVisible(true);
        form.resetFields();
        setActiveTab("details");
        // 重置已修改字段记录
        setModifiedFields({});
        // 重置 Tab 状态显示
        setTabStatus({ departureLanding: null, arrivalLanding: null, overflight: null });
    };

    // 记录用户修改的字段及其值
    const recordFieldChange = (fieldName, value) => {
        setModifiedFields((prev) => ({
            ...prev,
            [fieldName]: value,
        }));
    };

    // 根据ID筛选选中的航班详情
    const selectedFlightDetails = allFlightsData.filter((flight) => selectedFlightIds.includes(flight.id));

    // 前端事务类型常量到后端API响应键名的映射
    const getApiKeyForTransactionType = (transactionType) => {
        const mapping = {
            [TRANSACTION_TYPES.PERMIT_REQUEST]: "permit_requests",
            [TRANSACTION_TYPES.FUEL_REQUEST]: "fuel_requests",
            [TRANSACTION_TYPES.CATERING_ORDER]: "catering_orders",
            [TRANSACTION_TYPES.FLIGHT_BRIEF]: "flight_briefs",
            [TRANSACTION_TYPES.CREW_TRANSPORTATION]: "crew_transportations",
            [TRANSACTION_TYPES.HOTAC]: "hotacs",
        };
        return mapping[transactionType];
    };

    // 检测当前事务类型是否有待检查的迁移事务
    const hasPendingMigrationForCurrentType = () => {
        if (!currentAction.transactionType || !selectedFlightDetails.length) {
            return false;
        }

        const apiKey = getApiKeyForTransactionType(currentAction.transactionType);

        const hasPending = selectedFlightDetails.some((flight) => {
            const transactions = flight.transactions?.[apiKey];
            if (transactions && Array.isArray(transactions)) {
                const pendingTransactions = transactions.filter((transaction) => {
                    return transaction.migration_status === TRANSACTION_MIGRATION_STATUS.PENDING_REVIEW;
                });
                return pendingTransactions.length > 0;
            }
            return false;
        });
        return hasPending;
    };

    // 检测指定事务类型是否有待检查的迁移事务（不依赖 currentAction 状态）
    const hasPendingMigrationForTransactionType = (transactionType) => {
        if (!transactionType || !selectedFlightDetails.length) {
            return false;
        }

        const apiKey = getApiKeyForTransactionType(transactionType);

        const hasPending = selectedFlightDetails.some((flight) => {
            const transactions = flight.transactions?.[apiKey];
            if (transactions && Array.isArray(transactions)) {
                const pendingTransactions = transactions.filter((transaction) => {
                    return transaction.migration_status === TRANSACTION_MIGRATION_STATUS.PENDING_REVIEW;
                });
                return pendingTransactions.length > 0;
            }
            return false;
        });
        return hasPending;
    };

    // 获取当前事务类型的待检查迁移事务
    const getPendingMigrationTransactions = () => {
        if (!currentAction.transactionType || !selectedFlightDetails.length) return [];

        const apiKey = getApiKeyForTransactionType(currentAction.transactionType);
        const pendingTransactions = [];
        selectedFlightDetails.forEach((flight) => {
            const transactions = flight.transactions?.[apiKey];
            if (transactions && Array.isArray(transactions)) {
                transactions.forEach((transaction) => {
                    if (transaction.migration_status === TRANSACTION_MIGRATION_STATUS.PENDING_REVIEW) {
                        pendingTransactions.push({
                            ...transaction,
                            flight_number: flight.flight_number,
                            flight_id: flight.id,
                        });
                    }
                });
            }
        });
        return pendingTransactions;
    };

    // 事务类型到邮件模板代码的映射
    const TRANSACTION_EMAIL_TEMPLATE_MAP = {
        [TRANSACTION_TYPES.PERMIT_REQUEST]: "PERMIT_REQUEST_APPLICATION",
        [TRANSACTION_TYPES.FUEL_REQUEST]: "FUEL_REQUEST_APPLICATION",
        [TRANSACTION_TYPES.CATERING_ORDER]: "CATERING_ORDER_APPLICATION",
        [TRANSACTION_TYPES.FLIGHT_BRIEF]: "FLIGHT_BRIEF_APPLICATION",
        [TRANSACTION_TYPES.CREW_TRANSPORTATION]: "CREW_TRANSPORTATION_APPLICATION",
        [TRANSACTION_TYPES.HOTAC]: "HOTAC_APPLICATION",
    };

    // 直接使用API服务实现批量创建事务，避免创建hook实例
    const batchCreateTransactions = useCallback(
        async (flightIds, transactionType, data) => {
            if (!flightIds || !flightIds.length) return { success: [], failed: [] };

            try {
                // 确定正确的变更类型 - 使用常量而非硬编码
                const changeType = TRANSACTION_CHANGE_TYPES[transactionType]?.create;

                if (!changeType) {
                    throw new Error(`不支持的事务类型: ${transactionType}`);
                }

                const results = [];
                const failedResults = [];

                for (const id of flightIds) {
                    try {
                        // 构建变更命令 - 只包含用户修改的字段
                        const payload = {
                            details: data.details || {},
                        };

                        // 只有当status字段有值时才包含
                        if (data.status !== undefined) {
                            payload.status = data.status;
                        }

                        // 只有当remarks实际存在且被修改时才包含
                        if ("remarks" in modifiedFields) {
                            payload.remarks = data.remarks;
                        }

                        const command = {
                            flight_operation_id: id,
                            change_type: changeType,
                            payload: payload,
                            operator: {
                                type: "USER",
                                id: "current_user", // 这里应该使用实际的用户ID
                            },
                            trigger_source: "MONITOR_UI_BATCH",
                            // 只有当remarks实际存在且被修改时才包含
                            ...("remarks" in modifiedFields ? { remarks: data.remarks } : {}),
                        };

                        const response = await flightOperationTransactionService.createTransactionViaChange(command);

                        // 正确处理 axios 响应结构 - 检查批量操作的详细结果
                        if (response.data && response.data.success) {
                            // 检查是否有失败的项目
                            const responseData = response.data.data;
                            if (responseData && responseData.failed_items && responseData.failed_items.length > 0) {
                                // 有失败项目，从失败项目中查找当前航班
                                const failedItem = responseData.failed_items.find((item) => item.flight_id === id);
                                if (failedItem) {
                                    failedResults.push({ flightId: id, success: false, error: failedItem.error });
                                } else {
                                    // 在成功项目中查找
                                    const successItem = responseData.success_items && responseData.success_items.find((item) => item.flight_id === id);
                                    if (successItem) {
                                        results.push({ flightId: id, success: true, data: successItem });
                                    } else {
                                        // 既不在成功也不在失败中，可能是数据结构问题
                                        failedResults.push({ flightId: id, success: false, error: "未知处理结果" });
                                    }
                                }
                            } else {
                                // 没有失败项目，认为成功
                                results.push({ flightId: id, success: true, data: response.data.data });
                            }
                        } else {
                            const backendErrorMessage = (response.data && response.data.message) || response.message;
                            const errorMessage = backendErrorMessage || "创建失败";
                            failedResults.push({ flightId: id, success: false, error: errorMessage });
                        }
                    } catch (err) {
                        // 确保使用 err.message 或者更具体的后端错误
                        const backendError = err.response?.data?.detail || err.response?.data?.message;
                        const displayError = backendError || err.message || "创建时发生未知错误";
                        failedResults.push({ flightId: id, success: false, error: displayError });
                    }
                }

                // 报告结果
                if (results.length > 0) {
                    message.success(`成功为${results.length}个航班创建${TRANSACTION_TYPE_LABELS[transactionType]}`);
                }

                if (failedResults.length > 0) {
                    message.warning(`${failedResults.length}个航班创建${TRANSACTION_TYPE_LABELS[transactionType]}失败，请检查详情`);
                }

                return { success: results, failed: failedResults };
            } catch (err) {
                console.error(`批量创建${transactionType}事务失败:`, err);
                const backendError = err.response?.data?.detail || err.response?.data?.message;
                const displayError = backendError || err.message || "未知错误";
                message.error(`批量创建事务失败: ${displayError}`);
                return { success: [], failed: flightIds.map((id) => ({ flightId: id, success: false, error: displayError })) };
            }
        },
        [modifiedFields],
    );

    // 处理点击确认
    const handleConfirm = async () => {
        if (isSubmitting) return;

        try {
            setIsSubmitting(true);

            // 获取表单值
            const values = await form.validateFields();

            // 根据表单值构建事务数据
            const transactionData = {
                status: values.status,
                remarks: values.remarks,
                details: extractDetailsFromValues(values, currentAction.transactionType, modifiedFields),
            };

            // 直接调用批量创建方法，无需使用hook
            const batchSubmitResult = await batchCreateTransactions(selectedFlightIds, currentAction.transactionType, transactionData);

            // 显示处理结果
            if (batchSubmitResult && batchSubmitResult.success && batchSubmitResult.success.length > 0) {
                const successTotal = batchSubmitResult.success.length;
                const failedTotal = batchSubmitResult.failed ? batchSubmitResult.failed.length : 0;
                message.success(`成功处理${successTotal}个航班，失败${failedTotal}个`);

                // 调用刷新回调，以高亮更新的航班行
                if (onSuccess && typeof onSuccess === "function") {
                    // 获取成功处理的航班ID列表
                    const successFlightIds = batchSubmitResult.success.map((item) => item.flightId);

                    // 对每个成功的航班调用刷新函数
                    successFlightIds.forEach((flightId) => {
                        onSuccess(flightId);
                    });
                }
            } else {
                message.warning("没有航班被成功处理");
            }

            // 关闭弹窗
            setConfirmModalVisible(false);
        } catch (error) {
            console.error("表单验证失败:", error);
        } finally {
            setIsSubmitting(false);
        }
    };

    // 生成邮件业务上下文数据 - V2模式格式
    const generateEmailContextData = useCallback(() => {
        return {
            // V1模式兼容格式（保留原有结构）
            flightOperations: selectedFlightDetails,
            transactionType: currentAction.transactionType,

            // V2模式需要的contentBlocks格式
            contentBlocks: [
                {
                    id: "operation_flight_list",
                    type: "operationFlightList",
                    title: `${TRANSACTION_TYPE_LABELS[currentAction.transactionType]}申请航班列表`,
                    data: selectedFlightDetails,
                    // 添加业务来源信息
                    business_source: {
                        entity_type: "FlightOperation",
                        entity_ids: selectedFlightIds,
                    },
                    // 为Excel导出添加额外的上下文信息
                    transaction_info: {
                        transaction_type: currentAction.transactionType,
                        transaction_label: TRANSACTION_TYPE_LABELS[currentAction.transactionType],
                        batch_size: selectedFlightIds.length,
                    },
                },
            ],
        };
    }, [selectedFlightDetails, currentAction.transactionType, selectedFlightIds]);

    // 处理发送申请邮件
    const handleSendEmail = async () => {
        // 支持统一抽屉的事务类型
        const supportedTypes = [TRANSACTION_TYPES.FUEL_REQUEST, TRANSACTION_TYPES.PERMIT_REQUEST];

        if (supportedTypes.includes(currentAction.transactionType)) {
            setIsTransactionEmailDrawerVisible(true);
            return;
        }

        // 其他事务类型使用原有逻辑
        const templateCode = TRANSACTION_EMAIL_TEMPLATE_MAP[currentAction.transactionType];
        if (!templateCode) {
            message.error("当前事务类型暂不支持邮件申请");
            return;
        }

        setIsEmailLoading(true);
        try {
            // 从数据库获取模板
            const templateResponse = await emailTemplateApi.getTemplateByCodeWithRecipients(templateCode);
            setEmailTemplate(templateResponse.data);
            setIsEmailEditorVisible(true);
        } catch (error) {
            console.error("获取邮件模板失败:", error);
            message.error("获取邮件模板失败，请稍后重试");
        } finally {
            setIsEmailLoading(false);
        }
    };

    // 处理邮件发送 - 基于ScheduleDetailDrawer的实现模式
    const handleEmailSubmit = async (emailData) => {
        try {
            setIsSending(true);
            setSendingProgress(`正在发送${TRANSACTION_TYPE_LABELS[currentAction.transactionType]}申请邮件...`);

            // 转换前端数据格式为统一邮件API期望的格式
            const convertEmailField = (field) => {
                if (Array.isArray(field)) {
                    return field.length > 0 ? field.join(", ") : null;
                }
                return field || null;
            };

            // 构建收件人列表
            const recipients = [];

            // 添加主收件人
            if (emailData.to && emailData.to.length > 0) {
                emailData.to.forEach((email) => {
                    if (email && email.trim()) {
                        recipients.push({
                            email: email.trim(),
                            type: "TO",
                        });
                    }
                });
            }

            // 添加抄送收件人
            if (emailData.cc && emailData.cc.length > 0) {
                emailData.cc.forEach((email) => {
                    if (email && email.trim()) {
                        recipients.push({
                            email: email.trim(),
                            type: "CC",
                        });
                    }
                });
            }

            // 添加密送收件人
            if (emailData.bcc && emailData.bcc.length > 0) {
                emailData.bcc.forEach((email) => {
                    if (email && email.trim()) {
                        recipients.push({
                            email: email.trim(),
                            type: "BCC",
                        });
                    }
                });
            }

            if (recipients.length === 0) {
                message.error("请至少添加一个收件人");
                return;
            }

            // 构建业务关联信息
            // 修正：record_type 应该反映实际存储的记录类型，而不是业务分类
            // linked_record_id 将存储航班ID，所以 record_type 应该是 "FLIGHT_OPERATION"
            // link_type 用于区分具体的业务场景
            const businessLink = {
                record_id: 0, // 批量操作使用占位符，实际ID在 details.flight_operation_ids 中
                record_type: "FLIGHT_OPERATION", // 反映实际存储的记录类型
                link_type: `${currentAction.transactionType}_APPLICATION_BATCH`, // 业务场景分类
                details: {
                    flight_operation_ids: selectedFlightIds,
                    transaction_type: currentAction.transactionType,
                    transaction_label: TRANSACTION_TYPE_LABELS[currentAction.transactionType],
                    batch_size: selectedFlightIds.length,
                },
            };

            // 构建统一邮件API请求数据
            const requestData = {
                business_link: businessLink,
                recipients: recipients,
                subject: emailData.subject || `批量${TRANSACTION_TYPE_LABELS[currentAction.transactionType]}申请`,
                body_html: emailData.contentHtml || "",
                body_text: emailData.contentText || null,
                attachments: emailData.attachments || [],
                sender_user_id: 1, // TODO: 从当前用户上下文获取
                user_email_config_id: null, // 使用默认邮箱配置
                send_interval: 1.0, // 固定使用1.0秒间隔
                is_bulk_split: recipients.length > 1, // 多收件人时拆分发送
                content_block_context: emailData.contentBlockContext || {
                    template_code: TRANSACTION_EMAIL_TEMPLATE_MAP[currentAction.transactionType],
                    instances: [],
                    business_context_snapshot: generateEmailContextData(),
                },
            };

            console.log("[BatchTransactionActions] 发送统一邮件请求数据:", requestData);

            // 调用统一邮件API
            const response = await sendUniversalEmail(requestData);

            if (response.success) {
                message.success(response.message || `批量${TRANSACTION_TYPE_LABELS[currentAction.transactionType]}申请邮件发送成功`);

                // 关闭邮件编辑器
                setIsEmailEditorVisible(false);

                // 可选：刷新相关数据
                if (onSuccess && typeof onSuccess === "function") {
                    selectedFlightIds.forEach((flightId) => {
                        onSuccess(flightId);
                    });
                }
            } else {
                message.error(response.message || "邮件发送失败");
            }
        } catch (error) {
            console.error("发送邮件失败:", error);

            // 安全地提取错误消息
            let errorMessage = "发送邮件时发生错误";

            if (error.response?.data?.detail) {
                // 如果detail是数组（Pydantic验证错误），提取并格式化错误消息
                if (Array.isArray(error.response.data.detail)) {
                    const validationErrors = error.response.data.detail
                        .map((err) => {
                            const field = err.loc ? err.loc.join(".") : "未知字段";
                            return `${field}: ${err.msg}`;
                        })
                        .join("; ");
                    errorMessage = `数据验证失败: ${validationErrors}`;
                } else if (typeof error.response.data.detail === "string") {
                    errorMessage = error.response.data.detail;
                }
            } else if (error.response?.data?.message) {
                errorMessage = error.response.data.message;
            } else if (error.message) {
                errorMessage = error.message;
            }

            message.error(errorMessage);
        } finally {
            setIsSending(false);
            setSendingProgress(null);
        }
    };

    // 处理邮件编辑器取消
    const handleEmailCancel = () => {
        setIsEmailEditorVisible(false);
        setIsSending(false);
        setSendingProgress(null);
    };

    // 处理事务邮件抽屉关闭
    const handleTransactionEmailDrawerClose = () => {
        setIsTransactionEmailDrawerVisible(false);
    };

    // 处理事务邮件发送成功
    const handleTransactionEmailSuccess = (flightId) => {
        // 调用父组件的成功回调
        if (onSuccess && typeof onSuccess === "function") {
            onSuccess(flightId);
        }
    };

    // 从表单值中提取details字段 - 只提取用户实际修改的字段
    const extractDetailsFromValues = (values, transactionType, modifiedFields) => {
        // 创建一个空对象用于存储最终结果
        const result = {};

        // 针对不同事务类型进行特殊处理
        switch (transactionType) {
            case TRANSACTION_TYPES.PERMIT_REQUEST:
                // 许可申请需要特殊处理嵌套结构
                const departureLandingPermission = {};
                const arrivalLandingPermission = {};
                const overFlightPermission = {};

                // 只包含用户实际修改的字段
                if ("departureLandingStatus" in modifiedFields) {
                    departureLandingPermission.status = modifiedFields.departureLandingStatus;
                }
                if ("departureLandingReferenceNumber" in modifiedFields) {
                    departureLandingPermission.referenceNumber = modifiedFields.departureLandingReferenceNumber;
                }
                if ("departureLandingManifestNumber" in modifiedFields) {
                    departureLandingPermission.manifestNumber = modifiedFields.departureLandingManifestNumber;
                }
                if ("departureLandingRemarks" in modifiedFields) {
                    departureLandingPermission.remarks = modifiedFields.departureLandingRemarks;
                }

                if ("arrivalLandingStatus" in modifiedFields) {
                    arrivalLandingPermission.status = modifiedFields.arrivalLandingStatus;
                }
                if ("arrivalLandingReferenceNumber" in modifiedFields) {
                    arrivalLandingPermission.referenceNumber = modifiedFields.arrivalLandingReferenceNumber;
                }
                if ("arrivalLandingManifestNumber" in modifiedFields) {
                    arrivalLandingPermission.manifestNumber = modifiedFields.arrivalLandingManifestNumber;
                }
                if ("arrivalLandingRemarks" in modifiedFields) {
                    arrivalLandingPermission.remarks = modifiedFields.arrivalLandingRemarks;
                }

                if ("overFlightStatus" in modifiedFields) {
                    overFlightPermission.status = modifiedFields.overFlightStatus;
                }
                if ("overFlightReferenceNumber" in modifiedFields) {
                    overFlightPermission.referenceNumber = modifiedFields.overFlightReferenceNumber;
                }
                if ("overFlightStd" in modifiedFields) {
                    overFlightPermission.std = modifiedFields.overFlightStd ? (typeof modifiedFields.overFlightStd === "string" ? modifiedFields.overFlightStd : modifiedFields.overFlightStd.toISOString()) : null;
                }
                if ("overFlightSta" in modifiedFields) {
                    overFlightPermission.sta = modifiedFields.overFlightSta ? (typeof modifiedFields.overFlightSta === "string" ? modifiedFields.overFlightSta : modifiedFields.overFlightSta.toISOString()) : null;
                }
                if ("overFlightDelayHours" in modifiedFields) {
                    overFlightPermission.delayHours = modifiedFields.overFlightDelayHours;
                }
                if ("overFlightRemarks" in modifiedFields) {
                    overFlightPermission.remarks = modifiedFields.overFlightRemarks;
                }

                // 只有当对象中有属性时，才添加到结果中
                if (Object.keys(departureLandingPermission).length > 0) {
                    result.departureLandingPermission = departureLandingPermission;
                }
                if (Object.keys(arrivalLandingPermission).length > 0) {
                    result.arrivalLandingPermission = arrivalLandingPermission;
                }
                if (Object.keys(overFlightPermission).length > 0) {
                    result.overFlightPermission = overFlightPermission;
                }

                return result;

            case TRANSACTION_TYPES.FUEL_REQUEST:
                // 处理燃油申请字段
                if ("orderNumber" in modifiedFields) {
                    result.orderNumber = modifiedFields.orderNumber;
                }
                if ("expiryDate" in modifiedFields) {
                    result.expiryDate = modifiedFields.expiryDate ? (typeof modifiedFields.expiryDate === "string" ? modifiedFields.expiryDate : modifiedFields.expiryDate.toISOString()) : null;
                }
                if ("maxPayload" in modifiedFields) {
                    result.maxPayload = modifiedFields.maxPayload;
                }
                return result;

            case TRANSACTION_TYPES.CATERING_ORDER:
                // 处理配餐订单字段
                if ("version" in modifiedFields) {
                    result.version = modifiedFields.version; // 直接使用用户输入的值，包括空字符串
                }
                if ("crewNumber" in modifiedFields) {
                    result.crewNumber = modifiedFields.crewNumber;
                }
                if ("orderNo" in modifiedFields) {
                    result.orderNo = modifiedFields.orderNo;
                }
                return result;

            case TRANSACTION_TYPES.FLIGHT_BRIEF:
                // 处理飞行简报字段
                if ("maxPayload" in modifiedFields) {
                    result.maxPayload = modifiedFields.maxPayload;
                }
                if ("version" in modifiedFields) {
                    result.version = modifiedFields.version; // 直接使用用户输入的值，包括空字符串
                }
                return result;

            case TRANSACTION_TYPES.CREW_TRANSPORTATION:
                // 处理机组接送字段
                if ("route" in modifiedFields) {
                    result.route = modifiedFields.route;
                }
                if ("pickup_time" in modifiedFields) {
                    result.pickup_time = modifiedFields.pickup_time ? (typeof modifiedFields.pickup_time === "string" ? modifiedFields.pickup_time : modifiedFields.pickup_time.toISOString()) : null;
                }
                if ("pax_number" in modifiedFields) {
                    result.pax_number = modifiedFields.pax_number;
                }
                if ("version" in modifiedFields) {
                    result.version = modifiedFields.version; // 直接使用用户输入的值，包括空字符串
                }
                if ("order_number" in modifiedFields) {
                    result.order_number = modifiedFields.order_number;
                }
                return result;

            case TRANSACTION_TYPES.HOTAC:
                // 处理酒店住宿字段
                if ("pax_number" in modifiedFields) {
                    result.pax_number = modifiedFields.pax_number;
                }
                if ("version" in modifiedFields) {
                    result.version = modifiedFields.version; // 直接使用用户输入的值，包括空字符串
                }
                if ("order_number" in modifiedFields) {
                    result.order_number = modifiedFields.order_number;
                }
                return result;

            default:
                return {};
        }
    };

    // 监控字段变更，用于诊断
    const handleFieldsChange = (changedFields) => {
        // 这里不再需要，使用了更精确的recordFieldChange方法
    };

    // 事务状态选择器组件 - 增加清除状态功能
    const TransactionStatusSelector = ({ form, fieldName = "status", transactionType }) => {
        const currentStatus = Form.useWatch(fieldName, form);

        const handleStatusClick = (status) => {
            form.setFieldValue(fieldName, status);
            // 记录用户修改的字段及其值
            recordFieldChange(fieldName, status);
        };

        const handleClearStatus = () => {
            form.setFieldValue(fieldName, null);
            // 记录用户修改的字段及其值 (清除状态时值为null)
            recordFieldChange(fieldName, null);
        };

        // 根据事务类型获取状态列表
        const getStatusesForType = () => {
            if (transactionType && TRANSACTION_TYPE_SPECIFIC_STATUS[transactionType]) {
                return Object.values(TRANSACTION_TYPE_SPECIFIC_STATUS[transactionType]);
            }
            // 默认使用通用状态
            return Object.values(TRANSACTION_STATUS);
        };

        const statusOrder = getStatusesForType();

        return (
            <div style={{ display: "flex", flexDirection: "row", gap: "8px", flexWrap: "wrap" }}>
                {statusOrder.map((status) => {
                    const isActive = currentStatus === status;
                    return (
                        <Button
                            key={status}
                            type={isActive ? "primary" : "default"}
                            ghost={isActive}
                            size="small"
                            onClick={() => handleStatusClick(status)}
                            style={{
                                textAlign: "center",
                                padding: "0 12px",
                                minWidth: "auto", // 让按钮宽度自适应内容
                                flex: "none", // 不拉伸
                                color: isActive ? TRANSACTION_STATUS_COLORS[status] : undefined,
                                borderColor: isActive ? TRANSACTION_STATUS_COLORS[status] : undefined,
                            }}
                        >
                            {isActive && <CheckOutlined />} {TRANSACTION_STATUS_LABELS[status]}
                        </Button>
                    );
                })}
                {/* 添加清除按钮 */}
                <Button
                    type={currentStatus === null ? "primary" : "default"}
                    danger={currentStatus === null} // 仅在当前状态为null(已清除)时显示danger
                    ghost={currentStatus === null}
                    size="small"
                    onClick={handleClearStatus}
                    style={{
                        textAlign: "center",
                        padding: "0 12px",
                        minWidth: "auto",
                        flex: "none",
                    }}
                    icon={<CloseCircleOutlined />}
                >
                    清除状态
                </Button>
            </div>
        );
    };

    // Tab标签渲染函数 - 修改逻辑，仅在 status 有值时显示状态标签
    const renderTabTitle = (label, status) => (
        <span>
            {label}
            {status !== null &&
                status !== undefined && ( // 仅当status有实际值时渲染Tag
                    <TransactionStatusTag status={status} textOnly showIcon size="small" style={{ marginLeft: 6 }} />
                )}
        </span>
    );

    // 渲染表单字段
    const renderTransactionFields = () => {
        switch (currentAction.transactionType) {
            case TRANSACTION_TYPES.FUEL_REQUEST:
                return (
                    <>
                        <Form.Item label="订单编号" name="orderNumber">
                            <Input placeholder="输入燃油订单编号" onChange={(e) => recordFieldChange("orderNumber", e.target.value)} />
                        </Form.Item>
                        <Form.Item label="有效期" name="expiryDate">
                            <DatePicker placeholder="选择有效期" style={{ width: "100%" }} onChange={(date) => recordFieldChange("expiryDate", date)} />
                        </Form.Item>
                    </>
                );

            case TRANSACTION_TYPES.PERMIT_REQUEST:
                return (
                    <>
                        <Tabs defaultActiveKey="departureLanding" style={{ marginBottom: 16 }} className="transaction-detail-tabs">
                            <TabPane tab={renderTabTitle("始发机场起降许可", tabStatus.departureLanding)} key="departureLanding">
                                <Form.Item label="状态" name="departureLandingStatus">
                                    <TransactionStatusSelector form={form} fieldName="departureLandingStatus" transactionType={currentAction.transactionType} />
                                </Form.Item>
                                <Form.Item label="参考编号" name="departureLandingReferenceNumber">
                                    <Input placeholder="输入参考编号" onChange={(e) => recordFieldChange("departureLandingReferenceNumber", e.target.value)} />
                                </Form.Item>
                                <Form.Item label="舱单编号" name="departureLandingManifestNumber">
                                    <Input placeholder="输入舱单编号" onChange={(e) => recordFieldChange("departureLandingManifestNumber", e.target.value)} />
                                </Form.Item>
                            </TabPane>
                            <TabPane tab={renderTabTitle("飞越许可", tabStatus.overflight)} key="overflight">
                                <Form.Item label="状态" name="overFlightStatus">
                                    <TransactionStatusSelector form={form} fieldName="overFlightStatus" transactionType={currentAction.transactionType} />
                                </Form.Item>
                                <Form.Item label="参考编号" name="overFlightReferenceNumber">
                                    <Input placeholder="输入参考编号" onChange={(e) => recordFieldChange("overFlightReferenceNumber", e.target.value)} />
                                </Form.Item>
                                <Form.Item label="标准起飞时间" name="overFlightStd">
                                    <DatePicker showTime style={{ width: "100%" }} onChange={(date) => recordFieldChange("overFlightStd", date)} />
                                </Form.Item>
                                <Form.Item label="标准到达时间" name="overFlightSta">
                                    <DatePicker showTime style={{ width: "100%" }} onChange={(date) => recordFieldChange("overFlightSta", date)} />
                                </Form.Item>
                                <Form.Item label="延误小时数" name="overFlightDelayHours">
                                    <InputNumber min={0} max={72} style={{ width: "100%" }} onChange={(value) => recordFieldChange("overFlightDelayHours", value)} />
                                </Form.Item>
                            </TabPane>
                            <TabPane tab={renderTabTitle("目的机场起降许可", tabStatus.arrivalLanding)} key="arrivalLanding">
                                <Form.Item label="状态" name="arrivalLandingStatus">
                                    <TransactionStatusSelector form={form} fieldName="arrivalLandingStatus" transactionType={currentAction.transactionType} />
                                </Form.Item>
                                <Form.Item label="参考编号" name="arrivalLandingReferenceNumber">
                                    <Input placeholder="输入参考编号" onChange={(e) => recordFieldChange("arrivalLandingReferenceNumber", e.target.value)} />
                                </Form.Item>
                                <Form.Item label="舱单编号" name="arrivalLandingManifestNumber">
                                    <Input placeholder="输入舱单编号" onChange={(e) => recordFieldChange("arrivalLandingManifestNumber", e.target.value)} />
                                </Form.Item>
                            </TabPane>
                        </Tabs>
                    </>
                );

            case TRANSACTION_TYPES.CATERING_ORDER:
                return (
                    <>
                        <Form.Item label="版本" name="version">
                            <Input placeholder="输入版本号" onChange={(e) => recordFieldChange("version", e.target.value)} />
                        </Form.Item>
                        <Form.Item label="机组人数" name="crewNumber">
                            <InputNumber min={1} max={20} style={{ width: "100%" }} onChange={(value) => recordFieldChange("crewNumber", value)} />
                        </Form.Item>
                        <Form.Item label="订单编号" name="orderNo">
                            <Input placeholder="输入订单编号" onChange={(e) => recordFieldChange("orderNo", e.target.value)} />
                        </Form.Item>
                    </>
                );

            case TRANSACTION_TYPES.FLIGHT_BRIEF:
                return (
                    <>
                        <Form.Item label="最大载荷(吨)" name="maxPayload">
                            <InputNumber min={0} max={1000} step={0.1} style={{ width: "100%" }} onChange={(value) => recordFieldChange("maxPayload", value)} />
                        </Form.Item>
                        <Form.Item label="版本" name="version">
                            <Input placeholder="输入版本号" onChange={(e) => recordFieldChange("version", e.target.value)} />
                        </Form.Item>
                    </>
                );

            case TRANSACTION_TYPES.CREW_TRANSPORTATION:
                return (
                    <>
                        <Form.Item label="线路" name="route">
                            <Select
                                placeholder="选择接送线路"
                                options={[
                                    { value: "HOTEL_TO_AIRPORT", label: "H→A (酒店→机场)" },
                                    { value: "AIRPORT_TO_HOTEL", label: "A→H (机场→酒店)" },
                                ]}
                                onChange={(value) => recordFieldChange("route", value)}
                                style={{ width: "100%" }}
                            />
                        </Form.Item>
                        <Form.Item label="接送时间(LT)" name="pickup_time">
                            <DatePicker showTime placeholder="选择接送时间" style={{ width: "100%" }} onChange={(date) => recordFieldChange("pickup_time", date)} />
                        </Form.Item>
                        <Form.Item label="人数" name="pax_number">
                            <InputNumber min={1} max={30} placeholder="输入人数" style={{ width: "100%" }} onChange={(value) => recordFieldChange("pax_number", value)} />
                        </Form.Item>
                        <Form.Item label="版本" name="version">
                            <Input placeholder="输入版本号" onChange={(e) => recordFieldChange("version", e.target.value)} />
                        </Form.Item>
                    </>
                );

            case TRANSACTION_TYPES.HOTAC:
                return (
                    <>
                        <Form.Item label="人数" name="pax_number">
                            <InputNumber min={1} max={30} placeholder="输入人数" style={{ width: "100%" }} onChange={(value) => recordFieldChange("pax_number", value)} />
                        </Form.Item>
                        <Form.Item label="版本" name="version">
                            <Input placeholder="输入版本号" onChange={(e) => recordFieldChange("version", e.target.value)} />
                        </Form.Item>
                        <Form.Item label="订单编号" name="order_number">
                            <Input placeholder="输入订单编号" onChange={(e) => recordFieldChange("order_number", e.target.value)} />
                        </Form.Item>
                    </>
                );

            default:
                return <Empty description="没有可用的表单字段" />;
        }
    };

    // 确认弹窗标题
    const getConfirmModalTitle = () => {
        const transactionLabel = currentAction.transactionType ? TRANSACTION_TYPE_LABELS[currentAction.transactionType] : "事务";
        return `批量${transactionLabel} (${selectedFlightIds.length}个航班)`;
    };

    // 处理移除航班
    const handleRemoveFlight = (flightId) => {
        if (onFlightRemove && typeof onFlightRemove === "function") {
            onFlightRemove(flightId);
        }
    };

    // 批量确认当前事务类型的迁移
    const handleConfirmMigrationForCurrentType = async () => {
        const pendingTransactions = getPendingMigrationTransactions();
        if (pendingTransactions.length === 0) {
            message.warning("没有找到待检查的迁移事务");
            return;
        }

        setIsMigrationProcessing(true);
        try {
            let successCount = 0;
            let failedCount = 0;
            const errors = [];

            for (const transaction of pendingTransactions) {
                try {
                    await flightOperationTransactionService.confirmTransactionMigration(currentAction.transactionType, transaction.id);
                    successCount++;
                } catch (error) {
                    failedCount++;
                    errors.push(`航班${transaction.flight_number} 事务${transaction.id}确认失败: ${error.message}`);
                }
            }

            // 显示操作结果
            if (successCount > 0) {
                message.success(`成功确认${successCount}个${TRANSACTION_TYPE_LABELS[currentAction.transactionType]}迁移事务${failedCount > 0 ? `，失败${failedCount}个` : ""}`);

                // 刷新航班数据
                if (onSuccess && typeof onSuccess === "function") {
                    selectedFlightIds.forEach((flightId) => {
                        onSuccess(flightId);
                    });
                }

                // 如果是燃油或配餐事务，且迁移成功，继续打开新版本处理页面
                if ((currentAction.transactionType === TRANSACTION_TYPES.FUEL_REQUEST || currentAction.transactionType === TRANSACTION_TYPES.CATERING_ORDER) && onOpenBatchTransactionDrawer) {
                    const transactionType = currentAction.transactionType === TRANSACTION_TYPES.FUEL_REQUEST ? "FUEL_REQUEST" : "CATERING_ORDER";
                    onOpenBatchTransactionDrawer(transactionType);
                }
            } else if (failedCount > 0) {
                message.error(`所有${TRANSACTION_TYPE_LABELS[currentAction.transactionType]}迁移事务确认都失败了`);
            }

            if (errors.length > 0) {
                console.error("批量确认迁移错误:", errors);
            }

            // 无论成功与否都关闭Modal
            setCurrentAction({ visible: false, transactionType: null });
            setConfirmModalVisible(false);
        } catch (error) {
            console.error("批量确认迁移失败:", error);
            message.error(`批量确认迁移失败: ${error.message}`);
        } finally {
            setIsMigrationProcessing(false);
        }
    };

    // 批量拒绝当前事务类型的迁移
    const handleRejectMigrationForCurrentType = async () => {
        const pendingTransactions = getPendingMigrationTransactions();
        if (pendingTransactions.length === 0) {
            message.warning("没有找到待检查的迁移事务");
            return;
        }

        setIsMigrationProcessing(true);
        try {
            let successCount = 0;
            let failedCount = 0;
            const errors = [];

            for (const transaction of pendingTransactions) {
                try {
                    await flightOperationTransactionService.rejectTransactionMigration(currentAction.transactionType, transaction.id);
                    successCount++;
                } catch (error) {
                    failedCount++;
                    errors.push(`航班${transaction.flight_number} 事务${transaction.id}拒绝失败: ${error.message}`);
                }
            }

            // 显示操作结果
            if (successCount > 0) {
                message.success(`成功拒绝${successCount}个${TRANSACTION_TYPE_LABELS[currentAction.transactionType]}迁移事务${failedCount > 0 ? `，失败${failedCount}个` : ""}`);

                // 刷新航班数据
                if (onSuccess && typeof onSuccess === "function") {
                    selectedFlightIds.forEach((flightId) => {
                        onSuccess(flightId);
                    });
                }

                // 如果是燃油或配餐事务，且迁移处理完成，继续打开新版本处理页面
                if ((currentAction.transactionType === TRANSACTION_TYPES.FUEL_REQUEST || currentAction.transactionType === TRANSACTION_TYPES.CATERING_ORDER) && onOpenBatchTransactionDrawer) {
                    const transactionType = currentAction.transactionType === TRANSACTION_TYPES.FUEL_REQUEST ? "FUEL_REQUEST" : "CATERING_ORDER";
                    onOpenBatchTransactionDrawer(transactionType);
                }
            } else if (failedCount > 0) {
                message.error(`所有${TRANSACTION_TYPE_LABELS[currentAction.transactionType]}迁移事务拒绝都失败了`);
            }

            if (errors.length > 0) {
                console.error("批量拒绝迁移错误:", errors);
            }

            // 无论成功与否都关闭Modal
            setCurrentAction({ visible: false, transactionType: null });
            setConfirmModalVisible(false);
        } catch (error) {
            console.error("批量拒绝迁移失败:", error);
            message.error(`批量拒绝迁移失败: ${error.message}`);
        } finally {
            setIsMigrationProcessing(false);
        }
    };

    // 已修改字段展示组件
    const ModifiedFieldsDisplay = ({ modifiedFields, selectedFlights }) => {
        // 获取所有已修改字段的可读标签
        const getFieldLabel = (fieldName) => {
            const fieldLabels = {
                status: "状态",
                remarks: "备注",
                // 始发机场起降许可
                departureLandingStatus: "始发许可-状态",
                departureLandingReferenceNumber: "始发许可-参考编号",
                departureLandingManifestNumber: "始发许可-舱单编号",
                // 目的机场起降许可
                arrivalLandingStatus: "目的许可-状态",
                arrivalLandingReferenceNumber: "目的许可-参考编号",
                arrivalLandingManifestNumber: "目的许可-舱单编号",
                // 飞越许可
                overFlightStatus: "飞越许可-状态",
                overFlightReferenceNumber: "飞越许可-参考编号",
                overFlightStd: "飞越许可-标准起飞时间",
                overFlightSta: "飞越许可-标准到达时间",
                overFlightDelayHours: "飞越许可-延误小时数",
                // 燃油申请
                orderNumber: "订单编号",
                expiryDate: "有效期",
                // 通用
                maxPayload: "最大载荷",
                version: "版本",
                crewNumber: "机组人数",
                orderNo: "订单编号",
                // 机组接送
                route: "线路",
                pickup_time: "接送时间",
                pax_number: "人数",
                order_number: "订单编号",
            };

            return fieldLabels[fieldName] || fieldName;
        };

        // 格式化字段值以便显示
        const formatFieldValue = (fieldName, value) => {
            if (value === undefined || value === null) {
                return "无";
            }

            // 处理空字符串，显示为"(清空)"
            if (value === "") {
                return "(清空)";
            }

            // 处理日期类型
            if (fieldName.includes("Std") || fieldName.includes("Sta") || fieldName === "expiryDate") {
                if (typeof value === "object" && value.format) {
                    return value.format("YYYY-MM-DD HH:mm:ss");
                } else if (typeof value === "string") {
                    return value;
                }
            }

            // 处理状态类型
            if (fieldName.includes("Status") || fieldName === "status") {
                return TRANSACTION_STATUS_LABELS[value] || value;
            }

            // 最大显示10个字符
            if (String(value).length > 10) {
                return String(value).slice(0, 10) + "...";
            }
            return String(value);
        };

        return (
            <div style={{ marginBottom: 16 }}>
                <Alert
                    message={
                        <>
                            将对以下 <strong>{selectedFlights.length}个航班</strong> 批量更新
                        </>
                    }
                    description={
                        <>
                            <div style={{ maxHeight: "200px", overflowY: "auto", padding: "4px 0", overflowX: "hidden" }}>
                                <div style={{ display: "flex", flexDirection: "column", gap: "6px", alignItems: "flex-start" }}>
                                    {selectedFlights.map((flight, index) => (
                                        <Tag key={flight.id} closable onClose={() => handleRemoveFlight(flight.id)} color="blue" style={{ margin: 0, fontSize: "12px", lineHeight: "20px", width: "auto", maxWidth: "100%", textAlign: "left", whiteSpace: "normal", height: "auto", padding: "4px 8px", marginLeft: "4px" }}>
                                            {index + 1}. {flight.flight_number} ({flight.flight_date}) {flight.departure_airport}-{flight.arrival_airport}
                                        </Tag>
                                    ))}
                                </div>
                            </div>
                            <p>
                                <strong>覆盖更新</strong>以下信息：
                            </p>
                            <div>
                                {Object.keys(modifiedFields).map((fieldName) => (
                                    <Tooltip
                                        key={fieldName}
                                        title={
                                            <div>
                                                <div>
                                                    <strong>字段名称:</strong> {getFieldLabel(fieldName)}
                                                </div>
                                                <div>
                                                    <strong>修改后的值:</strong> {formatFieldValue(fieldName, modifiedFields[fieldName])}
                                                </div>
                                            </div>
                                        }
                                        placement="top"
                                    >
                                        <Tag color="blue" style={{ margin: "4px", cursor: "pointer" }}>
                                            <EditOutlined /> {getFieldLabel(fieldName)} → {formatFieldValue(fieldName, modifiedFields[fieldName])}
                                        </Tag>
                                    </Tooltip>
                                ))}
                            </div>
                        </>
                    }
                    type="info"
                    showIcon
                />
            </div>
        );
    };

    return (
        <PermissionGuard permission="flight_operation:update">
            {/* 添加一个隐藏的Form组件，确保useForm创建的实例始终与Form元素关联 */}
            <Form form={form} style={{ display: "none" }}>
                <Form.Item name="hidden">
                    <Input />
                </Form.Item>
            </Form>

            <Space size={12} wrap>
                <Tooltip title="批量创建/更新许可">
                    <Button icon={<FileProtectOutlined />} size="small" type="link" style={{ minWidth: 60 }} onClick={() => handleSelectTransaction(TRANSACTION_TYPES.PERMIT_REQUEST)}>
                        许可
                    </Button>
                </Tooltip>
                <Tooltip title="批量燃油申请 (V4版本 - 基于产品和合同)">
                    <Button
                        icon={<ThunderboltOutlined />}
                        size="small"
                        type="link"
                        style={{ minWidth: 60 }}
                        onClick={() => {
                            // 先检查是否有待检查的迁移事务（使用独立的检查函数）
                            if (hasPendingMigrationForTransactionType(TRANSACTION_TYPES.FUEL_REQUEST)) {
                                // 有待检查事务，设置当前事务类型并显示迁移处理弹窗
                                setCurrentAction({ transactionType: TRANSACTION_TYPES.FUEL_REQUEST });
                                setConfirmModalVisible(true);
                            } else {
                                // 没有待检查事务，直接调用V4版本的燃油申请处理函数
                                if (onOpenBatchTransactionDrawer) {
                                    onOpenBatchTransactionDrawer("FUEL_REQUEST");
                                } else {
                                    console.warn("V4燃油申请处理函数未找到");
                                    // 降级到原有处理方式
                                    handleSelectTransaction(TRANSACTION_TYPES.FUEL_REQUEST);
                                }
                            }
                        }}
                    >
                        燃油-新
                    </Button>
                </Tooltip>
                <Tooltip title="批量创建/更新简报">
                    <Button icon={<FileTextOutlined />} size="small" type="link" style={{ minWidth: 60 }} onClick={() => handleSelectTransaction(TRANSACTION_TYPES.FLIGHT_BRIEF)}>
                        简报
                    </Button>
                </Tooltip>
                <Tooltip title="批量配餐订单 (V4版本 - 基于产品和合同)">
                    <Button
                        icon={<CoffeeOutlined />}
                        size="small"
                        type="link"
                        style={{ minWidth: 60 }}
                        onClick={() => {
                            // 先检查是否有待检查的迁移事务（使用独立的检查函数）
                            if (hasPendingMigrationForTransactionType(TRANSACTION_TYPES.CATERING_ORDER)) {
                                // 有待检查事务，设置当前事务类型并显示迁移处理弹窗
                                setCurrentAction({ transactionType: TRANSACTION_TYPES.CATERING_ORDER });
                                setConfirmModalVisible(true);
                            } else {
                                // 没有待检查事务，直接调用V4版本的配餐申请处理函数
                                if (onOpenBatchTransactionDrawer) {
                                    onOpenBatchTransactionDrawer("CATERING_ORDER");
                                } else {
                                    console.warn("V4配餐申请处理函数未找到");
                                    // 降级到原有处理方式
                                    handleSelectTransaction(TRANSACTION_TYPES.CATERING_ORDER);
                                }
                            }
                        }}
                    >
                        配餐-新
                    </Button>
                </Tooltip>
                <Tooltip title="批量创建/更新机组接送">
                    <Button icon={<CarOutlined />} size="small" type="link" style={{ minWidth: 60 }} onClick={() => handleSelectTransaction(TRANSACTION_TYPES.CREW_TRANSPORTATION)}>
                        接送
                    </Button>
                </Tooltip>
                <Tooltip title="批量创建/更新酒店住宿">
                    <Button icon={<HomeOutlined />} size="small" type="link" style={{ minWidth: 60 }} onClick={() => handleSelectTransaction(TRANSACTION_TYPES.HOTAC)}>
                        住宿
                    </Button>
                </Tooltip>
            </Space>

            {/* 确认弹窗 */}
            <Modal
                title={getConfirmModalTitle()}
                open={confirmModalVisible}
                onCancel={() => setConfirmModalVisible(false)}
                maskClosable={false}
                destroyOnHidden
                width={600}
                footer={
                    hasPendingMigrationForCurrentType()
                        ? [
                              // 迁移管理模式的按钮
                              <Button key="cancel" onClick={() => setConfirmModalVisible(false)} disabled={isMigrationProcessing}>
                                  取消
                              </Button>,
                              <Button key="reject" icon={<CloseCircleOutlined />} onClick={handleRejectMigrationForCurrentType} loading={isMigrationProcessing} danger>
                                  拒绝迁移
                              </Button>,
                              <Button key="confirm" type="primary" icon={<CheckOutlined />} onClick={handleConfirmMigrationForCurrentType} loading={isMigrationProcessing}>
                                  确认迁移
                              </Button>,
                          ]
                        : [
                              // 正常编辑模式的按钮
                              <Button key="cancel" onClick={() => setConfirmModalVisible(false)} disabled={isSending}>
                                  取消
                              </Button>,
                              <Button key="email" icon={<MailOutlined />} onClick={handleSendEmail} loading={isEmailLoading || isSending} disabled={isSending}>
                                  发送申请邮件
                              </Button>,
                              <Button key="submit" type="primary" onClick={handleConfirm} loading={isSubmitting} disabled={Object.keys(modifiedFields).length === 0 || isSending}>
                                  确认提交
                              </Button>,
                              // 发送进度显示
                              sendingProgress && (
                                  <div key="progress" style={{ marginLeft: 12, color: "#1890ff", fontSize: "12px" }}>
                                      <span>{sendingProgress}</span>
                                  </div>
                              ),
                          ]
                }
            >
                {hasPendingMigrationForCurrentType() ? (
                    // 迁移管理模式：显示待检查的迁移事务信息
                    <div>
                        <Alert
                            message="发现待检查的迁移事务"
                            description={
                                <div>
                                    <p>
                                        选中的航班中有 <strong>{getPendingMigrationTransactions().length}</strong> 个{TRANSACTION_TYPE_LABELS[currentAction.transactionType]}事务需要迁移确认。
                                    </p>
                                    <p>在处理迁移事务之前，无法编辑事务内容。请选择确认迁移或拒绝迁移。</p>
                                    <div style={{ marginTop: 12 }}>
                                        <strong>待处理的事务：</strong>
                                        <ul style={{ marginTop: 8, marginBottom: 0 }}>
                                            {getPendingMigrationTransactions().map((transaction, index) => (
                                                <li key={index}>
                                                    航班 {transaction.flight_number} - 事务ID: {transaction.id}
                                                    {transaction.remarks && ` (${transaction.remarks})`}
                                                </li>
                                            ))}
                                        </ul>
                                    </div>
                                </div>
                            }
                            type="warning"
                            showIcon
                            style={{ marginBottom: 16 }}
                        />
                    </div>
                ) : (
                    // 正常编辑模式：显示表单内容
                    <Form form={form} layout="vertical" preserve={false} onFieldsChange={handleFieldsChange}>
                        {/* 显示已修改的字段 */}
                        <ModifiedFieldsDisplay modifiedFields={modifiedFields} selectedFlights={selectedFlightDetails} />

                        {/* 直接渲染表单内容，不再需要Tabs */}
                        {currentAction.transactionType !== TRANSACTION_TYPES.PERMIT_REQUEST && (
                            <Form.Item name="status" label="状态">
                                <TransactionStatusSelector form={form} fieldName="status" transactionType={currentAction.transactionType} />
                            </Form.Item>
                        )}
                        <Form.Item name="remarks" label="备注">
                            <TextArea rows={3} placeholder="输入批量申请备注" onChange={(e) => recordFieldChange("remarks", e.target.value)} />
                        </Form.Item>

                        {renderTransactionFields()}
                    </Form>
                )}
            </Modal>

            {/* 邮件编辑器 */}
            {isEmailEditorVisible && emailTemplate && (
                <GeneralEmailEditor
                    visible={isEmailEditorVisible}
                    onSubmit={handleEmailSubmit}
                    onCancel={handleEmailCancel}
                    title={`批量发送${TRANSACTION_TYPE_LABELS[currentAction.transactionType]}申请邮件 (${selectedFlightIds.length}个航班)`}
                    subjectTemplate={emailTemplate?.subject_template || `批量${TRANSACTION_TYPE_LABELS[currentAction.transactionType]}申请`}
                    templateContent={emailTemplate?.body_html || ""}
                    templateRecipients={{
                        default_to: emailTemplate?.default_to,
                        default_cc: emailTemplate?.default_cc,
                    }}
                    businessContext={generateEmailContextData()}
                    enableV2Mode={true}
                    // 传递发送状态给邮件编辑器
                    loading={isSending}
                    loadingText={sendingProgress}
                    // 发送过程中禁用取消操作
                    cancelDisabled={isSending}
                />
            )}

            {/* 统一事务邮件抽屉 */}
            <UnifiedTransactionEmailDrawer visible={isTransactionEmailDrawerVisible} onClose={handleTransactionEmailDrawerClose} selectedFlightIds={selectedFlightIds} allFlightsData={allFlightsData} transactionType={currentAction.transactionType} onSuccess={handleTransactionEmailSuccess} />
        </PermissionGuard>
    );
};

export default BatchTransactionActions;
